/**
 * ブラウザ拡張機能 (1Password 等) が page-context に inject したスクリプトから
 * 投げられる「(await browser.runtime.sendMessage(e)).message」系の TypeError を
 * 無害化フィルタする。
 *
 * 我々のアプリのバンドルには `browser.runtime.sendMessage` という識別子は
 * 一切含まれていないため、これにマッチするエラーは確実に拡張機能由来。
 *
 * 効果:
 *   - DevTools コンソールの赤いノイズを抑制
 *   - 将来 Sentry 等のエラー収集を入れたときに偽の通知を防ぐ
 *
 * 設置:
 *   index.tsx の冒頭で installExtensionErrorSuppressor() を 1 回だけ呼ぶ。
 */

const EXTENSION_ERROR_PATTERNS: RegExp[] = [
  /browser\.runtime\.sendMessage/i,
  /chrome\.runtime\.sendMessage/i,
  // 1Password / LastPass / Bitwarden などが投げる代表的なメッセージ片
  /Extension context invalidated/i,
];

function isExtensionError(message: string | undefined, filename: string | undefined): boolean {
  if (typeof message === "string") {
    for (const pattern of EXTENSION_ERROR_PATTERNS) {
      if (pattern.test(message)) return true;
    }
  }
  if (typeof filename === "string") {
    // 拡張機能由来 URL: chrome-extension:// / safari-web-extension:// / moz-extension://
    if (/^(?:chrome-extension|safari-web-extension|moz-extension):\/\//i.test(filename)) {
      return true;
    }
  }
  return false;
}

export function installExtensionErrorSuppressor(): void {
  if (typeof window === "undefined") return;

  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      const message = (event.error && (event.error as { message?: string }).message)
        ?? event.message;
      if (isExtensionError(message, event.filename)) {
        // 拡張機能由来のエラーはアプリと無関係なので、 unhandled error として
        // 伝播させない。フェイルセーフのため preventDefault も呼ぶ。
        event.preventDefault();
        event.stopImmediatePropagation();
        // 開発用に痕跡を残したい場合は console.debug にのみ落とす。
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[suppressExtensionErrors] suppressed:", message);
        }
      }
    },
    true,
  );

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const reason = event.reason as { message?: string; stack?: string } | undefined;
      const message = reason?.message ?? (typeof event.reason === "string" ? event.reason : undefined);
      const stack = reason?.stack;
      if (isExtensionError(message, stack)) {
        event.preventDefault();
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[suppressExtensionErrors] suppressed (rejection):", message);
        }
      }
    },
    true,
  );
}
